import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaMicrophone,
  FaStop,
  FaPlay,
  FaPause,
  FaUpload,
  FaSpinner,
  FaCheckCircle,
  FaInfoCircle,
  FaRedo,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ShadowingTask = {
  id: number;
  level: string;
  taskNumber: number;
  taskDescription: string;
  audioDownloadUrl: string;
};

type Submission = {
  id: number;
  audioDownloadUrl: string;
  submittedAt: string;
  reviewed: boolean;
  mentorFeedback: string;
};

const StudentShadowingTaskPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const { student } = useAuth();
  const navigate = useNavigate();

  const [task, setTask] = useState<ShadowingTask | null>(null);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);

  // Playback states
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingOriginal, setIsPlayingOriginal] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const originalAudioPlayerRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetchTask();
    checkExistingSubmission();
  }, [taskId]);

  useEffect(() => {
    return () => {
      // Cleanup
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const fetchTask = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/shadowing-tasks/${taskId}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setTask(data);
      } else {
        setError("Failed to load task details");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSubmission = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/shadowing-tasks/${taskId}/my-submission`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        if (data) {
          setSubmission(data);
        }
      }
    } catch (err) {
      console.log("No existing submission");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setError(
        "Could not access microphone. Please allow microphone permissions."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const playRecording = () => {
    if (audioUrl && audioPlayerRef.current) {
      audioPlayerRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseRecording = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    }
  };

  const playOriginal = () => {
    if (originalAudioPlayerRef.current) {
      originalAudioPlayerRef.current.play();
      setIsPlayingOriginal(true);
    }
  };

  const pauseOriginal = () => {
    if (originalAudioPlayerRef.current) {
      originalAudioPlayerRef.current.pause();
      setIsPlayingOriginal(false);
    }
  };

  const resetRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      setError("Please record your audio first");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const formData = new FormData();

      // Convert webm to mp3 filename for better compatibility
      const audioFile = new File([audioBlob], `recording_${Date.now()}.webm`, {
        type: "audio/webm",
      });
      formData.append("audioFile", audioFile);

      const endpoint = submission
        ? `${API_BASE_URL}/api/student/shadowing-tasks/${taskId}/resubmit`
        : `${API_BASE_URL}/api/student/shadowing-tasks/${taskId}/submit`;

      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setSubmission(data);
        setAudioBlob(null);
        setAudioUrl(null);
        alert(
          submission
            ? "Resubmitted successfully! Your mentor will review it."
            : "Submitted successfully! Your mentor will review it."
        );
      } else {
        const data = await response.json();
        setError(data.error || "Failed to submit recording");
      }
    } catch (err) {
      console.error("Error submitting:", err);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaMicrophone />}
        title="Shadowing Task"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Shadowing Tasks", to: "/student/my-shadowing-tasks" },
          { label: "Complete Task" },
        ]}
      />

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Instructions */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-6 mb-6 shadow-lg">
          <div className="flex items-start gap-4">
            <FaInfoCircle className="text-green-600 text-3xl flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-green-900 mb-3">
                How to Complete This Shadowing Task
              </h3>
              <div className="space-y-3 text-sm text-green-800 leading-relaxed">
                <p>
                  <strong>1.</strong> Listen to the original audio multiple
                  times
                </p>
                <p>
                  <strong>2.</strong> Practice speaking along with the audio
                  (shadowing technique)
                </p>
                <p>
                  <strong>3.</strong> When ready, click{" "}
                  <strong className="text-green-900">"Start Recording"</strong>{" "}
                  and record your version
                </p>
                <p>
                  <strong>4.</strong> Listen to your recording and re-record if
                  needed
                </p>
                <p>
                  <strong>5.</strong> Submit your best recording for mentor
                  review
                </p>
                <p className="text-base font-bold text-green-900 mt-4">
                  ✅ You can resubmit if you want to improve your recording
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Task Info */}
        {task && (
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <FaInfoCircle className="text-blue-600" />
              Task Information
            </h2>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700">Task #:</span>
                <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 font-bold rounded-md">
                  {task.taskNumber}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Level: </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  {task.level}
                </span>
              </div>
              {task.taskDescription && (
                <div className="mt-3">
                  <span className="font-semibold text-gray-700">
                    Description:{" "}
                  </span>
                  <p className="text-gray-800 mt-1">{task.taskDescription}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Original Audio Section */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaPlay className="text-[#009fe3]" />
              Original Audio
            </h2>

            {task?.audioDownloadUrl && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-3">
                    Listen to this audio multiple times and practice speaking
                    along:
                  </p>
                  <audio
                    ref={originalAudioPlayerRef}
                    controls
                    className="w-full"
                    src={task.audioDownloadUrl}
                    onPlay={() => setIsPlayingOriginal(true)}
                    onPause={() => setIsPlayingOriginal(false)}
                    onEnded={() => setIsPlayingOriginal(false)}
                  >
                    Your browser does not support audio playback.
                  </audio>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={isPlayingOriginal ? pauseOriginal : playOriginal}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    {isPlayingOriginal ? (
                      <>
                        <FaPause />
                        Pause
                      </>
                    ) : (
                      <>
                        <FaPlay />
                        Play Original
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Recording Section */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaMicrophone className="text-red-600" />
              Your Recording
            </h2>

            {!submission ? (
              <div className="space-y-4">
                {/* Recording Controls */}
                {!audioBlob ? (
                  <div className="space-y-4">
                    <div
                      className={`p-8 border-2 border-dashed rounded-lg text-center ${
                        isRecording
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {isRecording ? (
                        <>
                          <div className="flex justify-center mb-4">
                            <div className="relative">
                              <FaMicrophone className="text-red-600 text-6xl animate-pulse" />
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full animate-ping" />
                            </div>
                          </div>
                          <p className="text-lg font-bold text-red-600 mb-2">
                            Recording...
                          </p>
                          <p className="text-3xl font-mono text-gray-900">
                            {formatTime(recordingTime)}
                          </p>
                        </>
                      ) : (
                        <>
                          <FaMicrophone className="mx-auto text-gray-400 text-6xl mb-4" />
                          <p className="text-gray-600">Ready to record</p>
                          <p className="text-sm text-gray-500 mt-2">
                            Click "Start Recording" to begin
                          </p>
                        </>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {!isRecording ? (
                        <button
                          onClick={startRecording}
                          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
                        >
                          <FaMicrophone />
                          Start Recording
                        </button>
                      ) : (
                        <button
                          onClick={stopRecording}
                          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-colors"
                        >
                          <FaStop />
                          Stop Recording
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800 font-medium mb-3">
                        ✓ Recording Complete! ({formatTime(recordingTime)})
                      </p>
                      <audio
                        ref={audioPlayerRef}
                        controls
                        className="w-full"
                        src={audioUrl || undefined}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                      >
                        Your browser does not support audio playback.
                      </audio>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={isPlaying ? pauseRecording : playRecording}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                      >
                        {isPlaying ? (
                          <>
                            <FaPause />
                            Pause
                          </>
                        ) : (
                          <>
                            <FaPlay />
                            Play
                          </>
                        )}
                      </button>
                      <button
                        onClick={resetRecording}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                      >
                        <FaRedo />
                        Re-record
                      </button>
                    </div>

                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {submitting ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Submit Recording
                        </>
                      )}
                    </button>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCheckCircle className="text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Submission Received!
                    </h3>
                  </div>
                  <p className="text-sm text-green-700 mb-3">
                    Submitted on:{" "}
                    {new Date(submission.submittedAt).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>

                  <div className="mb-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Your Recording:
                    </p>
                    <audio
                      controls
                      className="w-full"
                      src={submission.audioDownloadUrl}
                    >
                      Your browser does not support audio playback.
                    </audio>
                  </div>

                  {submission.reviewed ? (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="font-semibold text-blue-900 mb-2">
                        ✓ Reviewed by Mentor
                      </p>
                      {submission.mentorFeedback && (
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-1">
                            Feedback:
                          </p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {submission.mentorFeedback}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⏳ Waiting for mentor review...
                      </p>
                    </div>
                  )}
                </div>

                {/* Resubmit Option */}
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Want to improve your recording? You can resubmit:
                  </p>
                  <button
                    onClick={() => setSubmission(null)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                  >
                    <FaRedo />
                    Record & Resubmit
                  </button>
                </div>

                <button
                  onClick={() => navigate("/student/my-shadowing-tasks")}
                  className="w-full px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
                >
                  Back to Shadowing Tasks
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentShadowingTaskPage;
